import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSearch,
  FaUndo,
} from "react-icons/fa";
import {
  MODULE_PAGE_SHELL_STYLE,
  TASK_PAGE_SHELL_CLASS,
} from "../../TaskManagement/workspaceConstants";
import {
  fetchCommissionStatusRules,
  updateCommissionStatusRule,
} from "./commissionsApi";
import { showClientActionErrorToast } from "./commissionsToast";
import "../../../styles/tailwind.css";
import { canViewCommissionModule } from "../../../utilis/invoicePermissionHelpers";

/** Fields the screen may change. status_value/label identify the rule. */
const EDITABLE_FIELDS = [
  "creates_membership",
  "eligibility",
  "eligibility_if_financials",
  "outcome",
  "workflow_status",
  "workflow_status_if_financials",
  "refund_status",
  "is_exception",
  "check_financials",
  "task_template",
  "task_priority",
  "is_active",
];

const SELECT_CLASS =
  "h-8 w-full min-w-[130px] rounded-md border border-gray-200 bg-white px-2 text-[12px] text-gray-800 focus:border-[#007BFF] focus:outline-none";

function LeaveUnchangedSelect({ value, options = [], onChange, title }) {
  return (
    <select
      className={SELECT_CLASS}
      value={value ?? ""}
      title={title}
      onChange={(event) => onChange(event.target.value || null)}
    >
      <option value="">— leave unchanged —</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="mb-0 inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-700">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

function RuleRow({ rule, options, onSaved }) {
  const [values, setValues] = useState(rule);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(rule);
  }, [rule]);

  const dirtyFields = useMemo(
    () =>
      EDITABLE_FIELDS.filter(
        (field) => (values?.[field] ?? null) !== (rule?.[field] ?? null),
      ),
    [values, rule],
  );

  const setField = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!dirtyFields.length) return;

    const payload = {};
    dirtyFields.forEach((field) => {
      payload[field] = values[field] ?? "";
    });

    setSaving(true);
    try {
      const result = await updateCommissionStatusRule(rule.id, payload);
      onSaved?.(result?.data ?? { ...rule, ...values }, result?.message);
    } catch (error) {
      showClientActionErrorToast(error?.message);
      setValues(rule);
    } finally {
      setSaving(false);
    }
  }, [dirtyFields, values, rule, onSaved]);

  const dirty = dirtyFields.length > 0;

  return (
    <tr
      className={`border-b border-gray-100 align-top ${
        dirty ? "bg-amber-50/60" : values.is_active ? "bg-white" : "bg-gray-50"
      }`}
    >
      <td className="px-2.5 py-2">
        <p className="mb-0 text-[12.5px] font-semibold text-gray-900">
          {rule.status_label}
        </p>
        <p className="mb-0 text-[11px] text-gray-500">
          Status value {rule.status_value}
          {rule.creates_membership ? " · entry trigger" : ""}
        </p>
      </td>

      <td className="px-2.5 py-2">
        <LeaveUnchangedSelect
          value={values.outcome}
          options={options.outcome}
          onChange={(v) => setField("outcome", v)}
          title="Student / enrolment outcome"
        />
      </td>

      <td className="px-2.5 py-2">
        <div className="space-y-1.5">
          <LeaveUnchangedSelect
            value={values.eligibility}
            options={options.eligibility}
            onChange={(v) => setField("eligibility", v)}
            title="Commission eligibility"
          />
          <LeaveUnchangedSelect
            value={values.eligibility_if_financials}
            options={options.eligibility}
            onChange={(v) => setField("eligibility_if_financials", v)}
            title="Used instead when money already exists"
          />
        </div>
      </td>

      <td className="px-2.5 py-2">
        <div className="space-y-1.5">
          <LeaveUnchangedSelect
            value={values.workflow_status}
            options={options.workflow_status}
            onChange={(v) => setField("workflow_status", v)}
            title="Commission status"
          />
          <LeaveUnchangedSelect
            value={values.workflow_status_if_financials}
            options={options.workflow_status}
            onChange={(v) => setField("workflow_status_if_financials", v)}
            title="Used instead when money already exists"
          />
        </div>
      </td>

      <td className="px-2.5 py-2">
        <LeaveUnchangedSelect
          value={values.refund_status}
          options={options.refund_status}
          onChange={(v) => setField("refund_status", v)}
          title="Refund status"
        />
      </td>

      <td className="px-2.5 py-2">
        <textarea
          rows={2}
          value={values.task_template ?? ""}
          placeholder="No task"
          onChange={(event) =>
            setField("task_template", event.target.value || null)
          }
          className="w-full min-w-[180px] rounded-md border border-gray-200 px-2 py-1 text-[12px] text-gray-800 focus:border-[#007BFF] focus:outline-none"
        />
        <select
          className={`${SELECT_CLASS} mt-1.5`}
          value={values.task_priority ?? ""}
          onChange={(event) =>
            setField("task_priority", event.target.value || null)
          }
        >
          <option value="">Priority —</option>
          <option value="standard">standard</option>
          <option value="high">high</option>
        </select>
      </td>

      <td className="px-2.5 py-2">
        <div className="space-y-1">
          <Toggle
            checked={values.is_active}
            onChange={(v) => setField("is_active", v)}
            label="Active"
          />
          <Toggle
            checked={values.creates_membership}
            onChange={(v) => setField("creates_membership", v)}
            label="Adds to Commissionable"
          />
          <Toggle
            checked={values.is_exception}
            onChange={(v) => setField("is_exception", v)}
            label="Exception"
          />
          <Toggle
            checked={values.check_financials}
            onChange={(v) => setField("check_financials", v)}
            label="Check money first"
          />
        </div>
      </td>

      <td className="px-2.5 py-2 text-right">
        <div className="flex flex-col items-end gap-1.5">
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={handleSave}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#007BFF] px-3 text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            style={{ border: "none", boxShadow: "none" }}
          >
            <FaCheck size={10} />
            {saving ? "Saving…" : "Save"}
          </button>
          {dirty ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => setValues(rule)}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 text-[11.5px] font-semibold text-gray-600"
              style={{ boxShadow: "none" }}
            >
              <FaUndo size={9} />
              Discard
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function CommissionRulesPage() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [options, setOptions] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCommissionStatusRules("education");
      setRules(result.rules);
      setOptions(result.options);
      setCounts(result.counts);
    } catch (err) {
      setError(err?.message || "Failed to load commission rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = useCallback((updated, message) => {
    setRules((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
    );
    setNotice(message || "Rule updated.");
    setTimeout(() => setNotice(null), 6000);
  }, []);

  const visibleRules = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rules.filter((rule) => {
      if (activeOnly && !rule.is_active) return false;
      if (!term) return true;

      return (
        String(rule.status_label).toLowerCase().includes(term) ||
        String(rule.status_value).includes(term) ||
        String(rule.outcome || "").toLowerCase().includes(term) ||
        String(rule.workflow_status || "").toLowerCase().includes(term)
      );
    });
  }, [rules, search, activeOnly]);

  if (!canViewCommissionModule()) {
    return (
      <div className="tailwind-container p-4">
        <p className="text-muted mb-0">
          You do not have permission to view the commission module.
        </p>
      </div>
    );
  }

  return (
    <div className="tailwind-container">
      <div className={TASK_PAGE_SHELL_CLASS} style={MODULE_PAGE_SHELL_STYLE}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate("/commissions-invoices")}
              className="mb-1.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#007BFF]"
              style={{ background: "none", border: "none", padding: 0 }}
            >
              <FaArrowLeft size={10} />
              Back to Commissions
            </button>
            <h5 className="mb-0.5 text-[17px] font-bold text-gray-900">
              Commission status automation
            </h5>
          
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-gray-100 px-2.5 py-1 text-[12px] font-semibold text-gray-700">
              {counts.active ?? 0} active of {counts.total ?? 0}
            </span>
            <span className="rounded-md bg-rose-50 px-2.5 py-1 text-[12px] font-semibold text-rose-700">
              {counts.exceptions ?? 0} exceptions
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5">
          <FaInfoCircle size={13} className="mt-0.5 shrink-0 text-sky-600" />
          <p className="mb-0 text-[12.5px] leading-snug text-sky-900">
            <strong>“Leave unchanged”</strong> means this status will not touch that
            field — which is how a student who is already Partially Paid keeps that
            position when their application status moves on. The{" "}
            <strong>second dropdown</strong> in Eligibility and Commission Status is
            used instead of the first whenever commission or tuition has already been
            recorded. No rule can alter a recorded amount.
          </p>
        </div>

        {notice ? (
          <div className="mt-2.5 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] font-semibold text-emerald-800">
            <FaCheck size={11} />
            {notice}
          </div>
        ) : null}

        {error ? (
          <div className="mt-2.5 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] font-semibold text-rose-800">
            <FaExclamationTriangle size={11} />
            {error}
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <FaSearch
              size={11}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search status, outcome or commission status"
              className="h-9 w-[320px] rounded-md border border-gray-200 pl-8 pr-2.5 text-[13px] focus:border-[#007BFF] focus:outline-none"
            />
          </div>
          <Toggle
            checked={activeOnly}
            onChange={setActiveOnly}
            label="Active rules only"
          />
          <span className="text-[12px] text-gray-500">
            Showing {visibleRules.length} of {rules.length}
          </span>
        </div>

        <div className="mt-3 overflow-x-auto rounded-md border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[1180px] text-[12.5px]">
            <thead className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-2.5 py-2 text-left">Application status</th>
                <th className="px-2.5 py-2 text-left">Student outcome</th>
                <th className="px-2.5 py-2 text-left">
                  Eligibility
                  <span className="block font-normal normal-case text-gray-400">
                    normal / if money exists
                  </span>
                </th>
                <th className="px-2.5 py-2 text-left">
                  Commission status
                  <span className="block font-normal normal-case text-gray-400">
                    normal / if money exists
                  </span>
                </th>
                <th className="px-2.5 py-2 text-left">Refund</th>
                <th className="px-2.5 py-2 text-left">Auto task</th>
                <th className="px-2.5 py-2 text-left">Behaviour</th>
                <th className="px-2.5 py-2 text-right">Save</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    Loading rules…
                  </td>
                </tr>
              ) : visibleRules.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                    No rules match this search.
                  </td>
                </tr>
              ) : (
                visibleRules.map((rule) => (
                  <RuleRow
                    key={rule.id}
                    rule={rule}
                    options={options}
                    onSaved={handleSaved}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CommissionRulesPage;
